import './Header.css';
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, m } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import Wordmark from "../ui/Wordmark";
import StoreButtons from "../ui/StoreButtons";
import GetAppLink from "../ui/GetAppLink";
import { FEATURE_GROUPS, featuresIn } from "../../content/features";
import { trackCta } from "../../utils/tracking";

const navItems = [
    { label: "Prepaid card", to: "/card" },
    { label: "Marketplace", to: "/marketplace" },
    { label: "Assistant", to: "/ai-agents", badge: "AI" },
    { label: "Blog", to: "/blog" },
    { label: "About", to: "/about-us" },
];

function NavItem({ item, onClick, large = false }) {
    return (
        <NavLink
            to={item.to}
            end={item.to === "/"}
            onClick={onClick}
            className={({ isActive }) =>
                large
                    ? `flex items-center justify-between border-b border-line py-4 font-serif text-[26px] font-medium tracking-[-0.01em] ${isActive ? "text-brand-pale-ink" : "text-ink"}`
                    : `nav-link${isActive ? " nav-link--active" : ""}`
            }
        >
            <span className="inline-flex items-center gap-2">
                {item.label}
                {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
            </span>
            {large ? <ArrowRight size={20} className="text-muted" /> : null}
        </NavLink>
    );
}

function FeaturesMenu() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const closeTimer = useRef(null);
    const location = useLocation();

    useEffect(() => setOpen(false), [location.pathname, location.hash]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onClick);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onClick);
        };
    }, [open]);

    const enter = () => {
        clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const leave = () => {
        closeTimer.current = setTimeout(() => setOpen(false), 140);
    };

    return (
        <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
            <button
                type="button"
                className={`nav-link inline-flex items-center gap-1${location.pathname === "/features" ? " nav-link--active" : ""}`}
                aria-expanded={open}
                aria-haspopup="true"
                onClick={() => setOpen((v) => !v)}
            >
                Features
                <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
                {open ? (
                    <m.div
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 6, scale: 0.98 }}
                        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute left-1/2 top-[calc(100%+18px)] w-[640px] -translate-x-1/2 rounded-xl border border-line bg-card p-2 shadow-lift"
                    >
                        <div className="grid grid-cols-2 gap-1">
                            {FEATURE_GROUPS.map((g) => {
                                const first = featuresIn(g.id)[0];
                                const Icon = first?.Icon;
                                return (
                                    <Link
                                        key={g.id}
                                        to={`/features#${g.id}`}
                                        className="group flex gap-3 rounded-lg p-3 transition-colors hover:bg-canvas"
                                    >
                                        <span className="ed-tile mt-0.5 h-9 w-9">
                                            {Icon ? <Icon size={18} /> : null}
                                        </span>
                                        <span>
                                            <span className="block text-[14px] font-semibold text-ink">{g.label}</span>
                                            <span className="mt-0.5 block text-[12.5px] leading-5 text-muted">
                                                {featuresIn(g.id).map((f) => f.title).slice(0, 3).join(" · ")}
                                            </span>
                                        </span>
                                    </Link>
                                );
                            })}
                            <Link
                                to="/features"
                                className="flex items-center justify-between rounded-lg border border-brand-pale-line bg-brand-pale p-3 text-[14px] font-semibold text-brand-pale-ink transition-colors hover:border-brand/40"
                            >
                                See every feature
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </m.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}

export default function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setIsOpen(false), [location.pathname]);

    // No page scroll behind the mobile menu.
    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    const closeMenu = () => setIsOpen(false);

    return (
        <header className={`site-header fixed left-0 right-0 top-0 z-header h-[72px]${scrolled || isOpen ? " site-header--scrolled" : ""}${isOpen ? " site-header--open" : ""}`}>
            <a href="#main-content" className="skip-link">Skip to content</a>
            <div className="relative mx-auto flex h-full w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link to="/" onClick={closeMenu} className="flex shrink-0 items-center" aria-label="Accruewise home">
                    <Wordmark />
                </Link>

                <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-7 xl:flex" aria-label="Primary">
                    <FeaturesMenu />
                    {navItems.map((item) => (
                        <NavItem key={item.label} item={item} />
                    ))}
                </nav>

                <div className="ml-auto mr-2 hidden shrink-0 items-center gap-2 lg:flex xl:ml-0 xl:mr-0">
                    <Link
                        to="/waitlist"
                        className="btn btn-md btn-secondary hidden xl:inline-flex"
                        onClick={() => trackCta("header", { cta_label: "waitlist" })}
                    >
                        Join waitlist
                    </Link>
                    <GetAppLink placement="header" className="btn btn-md btn-primary">
                        Get the app
                    </GetAppLink>
                </div>

                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md text-ink xl:hidden"
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isOpen}
                    aria-controls="mobile-menu"
                >
                    {isOpen ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {isOpen ? (
                    <m.div
                        id="mobile-menu"
                        key="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="fixed inset-x-0 bottom-0 top-[72px] z-sheet overflow-y-auto bg-canvas px-5 pb-10 pt-2 xl:hidden"
                    >
                        <m.nav
                            aria-label="Mobile"
                            initial={{ y: 12 }}
                            animate={{ y: 0 }}
                            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <NavItem item={{ label: "Features", to: "/features" }} onClick={closeMenu} large />
                            {navItems.map((item) => (
                                <NavItem key={item.label} item={item} onClick={closeMenu} large />
                            ))}
                            <NavItem item={{ label: "FAQs", to: "/faqs" }} onClick={closeMenu} large />
                            <NavItem item={{ label: "Contact", to: "/contact-us" }} onClick={closeMenu} large />
                        </m.nav>
                        <StoreButtons placement="header_mobile" className="mt-8" />
                    </m.div>
                ) : null}
            </AnimatePresence>
        </header>
    );
}
